import { execute, isLoaded, load } from '@arcgis/core/geometry/operators/projectOperator.js';
import Point from '@arcgis/core/geometry/Point';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import type { GeolocationDetails } from '@dte/otw-azure-functions/src/common/interfaces/api/Location';
import {
	validateLatitude,
	validateLongitude,
} from '@dte/otw-azure-functions/src/common/util/validation/validateGeolocation';
import { safeParseInt } from '@dte/otw-utils-core/src/numbers/safeParseInt';
import { normalizeGLN } from './formatGLN';

// Preload the projection utility
void load();

export function getGPSFromGLN(glnx: number | undefined, glny: number | undefined): GeolocationDetails | undefined {
	// Make sure we have valid values for X and Y
	if (glnx === undefined || !Number.isInteger(glnx)) {
		return undefined;
	}
	if (glny === undefined || !Number.isInteger(glny)) {
		return undefined;
	}

	// GLNX / GLNY need adjustments for projection

	let glnxAdjusted: number;
	// If GNLX is greater than 800000, add 1,000,000 to it.
	if (glnx > 800000) {
		glnxAdjusted = glnx + 1000000;
	}
	// If GNLX is less than or equal to 800000, add 2,000,000 to it.
	else {
		glnxAdjusted = glnx + 2000000;
	}

	try {
		if (!isLoaded()) {
			// TODO: is there an alternative here other than making this async?
			console.warn('Project operator not loaded yet');
		}
		const result = execute(
			new Point({ x: glnxAdjusted, y: glny, spatialReference: { wkid: 26790 } }),
			SpatialReference.WGS84,
		) as Point;
		const longitude = validateLongitude(result?.longitude);
		if (longitude === undefined) {
			return undefined;
		}
		const latitude = validateLatitude(result?.latitude);
		if (latitude === undefined) {
			return undefined;
		}
		return { longitude: longitude, latitude: latitude };
	} catch (error) {
		console.error('Error converting GLN to GPS:', error);
		return undefined;
	}
}

function getGLNFromString(glnString: string | undefined): [number | undefined, number | undefined] {
	const glnStringValue = normalizeGLN(glnString);

	// Not a valid GLN until we have at least 12 characters
	const stringLength = glnStringValue?.length || 0;
	if (stringLength < 12) {
		return [undefined, undefined];
	}

	const x = safeParseInt(glnStringValue.slice(0, 6));
	const y = safeParseInt(glnStringValue.slice(6));

	return [x, y];
}

export function getGPSFromGLNString(glnString: string | undefined): GeolocationDetails | undefined {
	const [glnx, glny] = getGLNFromString(glnString);
	return getGPSFromGLN(glnx, glny);
}
